import { useEffect, useMemo, useState } from "react";

// Generic, scalable collection controller: search + category filter + sort + "load more".
// Drives every content hub (Journal, Practice Library, Film Room, Clinics, Media) so the
// UX stays clean no matter how many items are added over the years.
export function useCollection(
  items,
  {
    pageSize = 6,
    searchKeys = ["title", "excerpt", "category", "tags"],
    initialCategory = "All",
    initialSort = "featured",
  } = {}
) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(initialCategory);
  const [sort, setSort] = useState(initialSort);
  const [visible, setVisible] = useState(pageSize);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))],
    [items]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let res = items.filter((it) => {
      const matchCat = category === "All" || it.category === category;
      if (!matchCat) return false;
      if (!q) return true;
      const hay = searchKeys
        .map((k) => {
          const v = it[k];
          return Array.isArray(v) ? v.join(" ") : v || "";
        })
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });

    if (sort === "newest") {
      res = [...res].sort((a, b) => (b.order ?? 0) - (a.order ?? 0));
    } else if (sort === "oldest") {
      res = [...res].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    } else if (sort === "featured") {
      res = [...res].sort(
        (a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || (b.order ?? 0) - (a.order ?? 0)
      );
    }
    return res;
  }, [items, category, query, sort, searchKeys]);

  useEffect(() => {
    setVisible(pageSize);
  }, [query, category, sort, pageSize]);

  const visibleItems = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  return {
    query,
    setQuery,
    category,
    setCategory,
    sort,
    setSort,
    categories,
    filtered,
    visibleItems,
    hasMore,
    total: filtered.length,
    loadMore: () => setVisible((v) => v + pageSize),
    reset: () => {
      setQuery("");
      setCategory(initialCategory);
      setSort(initialSort);
      setVisible(pageSize);
    },
  };
}
